"use client";

import { useRouter } from "next/navigation";
import { useRef, useTransition, type FormEvent } from "react";
import { FieldLabel, Input, Select } from "@/components/ui/form-field";
import { buttonClass } from "@/components/ui/button";
import type { Dictionary } from "@/i18n/dictionaries/tr";
import { fmt } from "@/i18n/config";
import type { Facets, TourQuery } from "../filters";
import { parseQuery, PRICE_CURRENCY } from "../filters";
import { listingHref, paramsToRecord, type ListingNav } from "../listing-url";
import { trackSearch } from "@/features/analytics/track";

export type FilterTexts = Dictionary["filters"];

// Filtre formu GET ile liste adresine (/turlar, /en/tours) gider: JavaScript olmadan da çalışır. JavaScript varsa boş alanlar
// URL'ye yazılmadan istemci tarafı gezinmeyle gönderilir. Masaüstünde her değişiklikte, mobil panelde alt çubuktaki
// "N turu göster" ile (panel her değişiklikte sonuç sayısını sorar: onChangeParams). `sayfa` forma eklenmez → filtre
// değişince sayfa 1'e döner. Yalnızca bölge, ürün ailesi ya da destinasyon seçiliyse o filtrenin sayfasına gidilir
// (/avrupa-turlari; listing-url.ts); JavaScript yoksa form /turlar'a gider ve sunucu aynı sayfaya 308 verir. Kısa seçenek listeleri (ürün ailesi, süre, fiyat, vizesiz) çip; uzun listeler biçimli select.

export function cleanParams(form: HTMLFormElement): string {
  const params = new URLSearchParams();
  for (const [key, value] of new FormData(form)) {
    if (typeof value === "string" && value.trim()) params.append(key, value.trim());
  }
  return params.toString();
}

type Props = {
  /** Bu dildeki liste adresi ve filtre sayfaları */
  nav: ListingNav;
  t: FilterTexts;
  query: TourQuery;
  facets: Facets;
  idPrefix: string;
  autoSubmit: boolean;
  onSubmitted?: () => void;
  /** Form kimliği (dışarıdaki gönder düğmesi: <button form=…>) */
  formId?: string;
  /** Gönder düğmesi formun dışında (mobil panelin alt çubuğu) */
  externalSubmit?: boolean;
  /** Her değişiklikte güncel sorgu metni (canlı sonuç sayısı) */
  onChangeParams?: (qs: string) => void;
};

export function FilterForm({ nav, t, query, facets, idPrefix, autoSubmit, onSubmitted, formId, externalSubmit, onChangeParams }: Props) {
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const changed = () => {
    if (autoSubmit) formRef.current?.requestSubmit();
    else if (formRef.current) onChangeParams?.(cleanParams(formRef.current));
  };
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const submitted = new URLSearchParams(cleanParams(e.currentTarget));
    trackSearch("listing_filters", submitted);
    const { query: next } = parseQuery(paramsToRecord(submitted));
    startTransition(() => router.push(listingHref(next, nav), { scroll: false }));
    onSubmitted?.();
  };
  const id = (name: string) => `${idPrefix}-${name}`;

  return (
    <form
      ref={formRef}
      id={formId}
      action={nav.list}
      method="get"
      onSubmit={onSubmit}
      className="grid gap-6"
      aria-label={t.formAria}
      aria-busy={pending}
    >
      <p aria-live="polite" className="sr-only">
        {pending ? t.updating : ""}
      </p>
      {query.sirala !== "tarih" && <input type="hidden" name="sirala" value={query.sirala} />}

      <div className="grid min-w-0 gap-1.5">
        <FieldLabel htmlFor={id("q")}>{t.query}</FieldLabel>
        <Input
          id={id("q")}
          name="q"
          type="search"
          defaultValue={query.q ?? ""}
          placeholder={t.queryPlaceholder}
          onChange={() => !autoSubmit && changed()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              formRef.current?.requestSubmit();
            }
          }}
        />
      </div>

      {/* Tur kodu: kısmi kod listeyi daraltır; tam tur/kalkış kodunda liste sayfası tura yönlendirir (tour-code.ts) */}
      <div className="grid min-w-0 gap-1.5">
        <div className="grid">
          <FieldLabel htmlFor={id("kod")}>{t.code}</FieldLabel>
          <span id={id("kod-hint")} className="text-xs text-ink-muted">
            {t.codeHint}
          </span>
        </div>
        <Input
          id={id("kod")}
          name="kod"
          type="search"
          aria-describedby={id("kod-hint")}
          defaultValue={query.kod ?? ""}
          placeholder={t.codePlaceholder}
          maxLength={40}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          enterKeyHint="search"
          className="not-placeholder-shown:font-semibold not-placeholder-shown:tracking-wide not-placeholder-shown:uppercase"
          onChange={() => !autoSubmit && changed()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              formRef.current?.requestSubmit();
            }
          }}
        />
      </div>

      <SelectGroup all={t.all} label={t.region} name="bolge" id={id("bolge")} value={query.bolge} options={facets.bolge} onChange={changed} />
      <SelectGroup all={t.all} label={t.destination} name="kategori" id={id("kategori")} value={query.kategori} options={facets.kategori} onChange={changed} />
      <SelectGroup all={t.all} label={t.month} name="ay" id={id("ay")} value={query.ay} options={facets.ay} onChange={changed} />
      <SelectGroup all={t.all} label={t.airport} name="kalkis" id={id("kalkis")} value={query.kalkis} options={facets.kalkis} onChange={changed} />
      <SelectGroup all={t.all} label={t.airline} name="havayolu" id={id("havayolu")} value={query.havayolu} options={facets.havayolu} onChange={changed} />
      <Chips all={t.all} label={t.family} name="aile" id={id("aile")} value={query.aile} options={facets.aile} onChange={changed} />
      <Chips all={t.all} label={t.duration} name="sure" id={id("sure")} value={query.sure} options={facets.sure} onChange={changed} />
      <Chips
        all={t.all}
        label={t.price}
        hint={fmt(t.priceHint, { currency: PRICE_CURRENCY })}
        name="fiyat"
        id={id("fiyat")}
        value={query.fiyat}
        options={facets.fiyat}
        onChange={changed}
      />

      {(facets.vizesiz > 0 || query.vizesiz) && (
        <div className="-mx-2.5">
          <label htmlFor={id("vizesiz")} className="option-row">
            <input id={id("vizesiz")} type="checkbox" name="vizesiz" value="1" defaultChecked={query.vizesiz} onChange={changed} />
            {t.visaFree}
            <span className="option-count">{facets.vizesiz}</span>
          </label>
        </div>
      )}

      {!externalSubmit && (
        <button type="submit" className={buttonClass("primary", "md", autoSubmit ? "sr-only focus:not-sr-only" : "w-full")}>
          {autoSubmit ? t.apply : t.showResults}
        </button>
      )}
    </form>
  );
}

type OptionProps = {
  /** "Tümü" seçeneğinin metni */
  all: string;
  label: string;
  hint?: string;
  name: string;
  id: string;
  value: string | undefined;
  options: { value: string; label: string; count: number }[];
  onChange: () => void;
};

function SelectGroup({ all, label, name, id, value, options, onChange }: OptionProps) {
  // Seçili değer sonuçsuz kaldıysa da listede görünsün (kullanıcı neyi seçtiğini görsün)
  const list = value && !options.some((o) => o.value === value) ? [...options, { value, label: value, count: 0 }] : options;
  return (
    <div className="grid min-w-0 gap-1.5">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select id={id} name={name} defaultValue={value ?? ""} onChange={onChange}>
        <option value="">{all}</option>
        {list.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label} ({o.count})
          </option>
        ))}
      </Select>
    </div>
  );
}

/** Radyo düğmeleri alt alta seçenek satırı olarak (fieldset + legend; yerel radio, klavyede oklarla; sayı sağda) */
function Chips({ all, label, hint, name, id, value, options, onChange }: OptionProps) {
  if (options.length === 0 && !value) return null;
  return (
    <fieldset className="min-w-0">
      <legend className="mb-2 text-sm font-semibold text-ink">
        {label}
        {hint && <span className="block text-xs font-normal text-ink-muted">{hint}</span>}
      </legend>
      <div className="-mx-2.5 grid gap-0.5">
        {[{ value: "", label: all, count: -1 }, ...options].map((o) => (
          <label key={o.value || "all"} htmlFor={`${id}-${o.value || "all"}`} className="option-row">
            <input
              id={`${id}-${o.value || "all"}`}
              type="radio"
              name={name}
              value={o.value}
              defaultChecked={(value ?? "") === o.value}
              onChange={onChange}
            />
            {o.label}
            {o.count >= 0 && <span className="option-count">{o.count}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
